/** Pure ImageData geometry — canvas-free so it runs in workers and Node. */

function blank(width: number, height: number, fill = 255): ImageData {
  const data = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = data[i + 1] = data[i + 2] = fill;
    data[i + 3] = 255;
  }
  return { data, width, height, colorSpace: "srgb" } as ImageData;
}

export function cropImageData(
  src: ImageData,
  x: number,
  y: number,
  width: number,
  height: number,
): ImageData {
  const sx = Math.max(0, Math.floor(x));
  const sy = Math.max(0, Math.floor(y));
  const w = Math.min(Math.floor(width), src.width - sx);
  const h = Math.min(Math.floor(height), src.height - sy);
  const out = blank(w, h);
  for (let row = 0; row < h; row++) {
    const from = ((sy + row) * src.width + sx) * 4;
    out.data.set(src.data.subarray(from, from + w * 4), row * w * 4);
  }
  return out;
}

/** Nearest-neighbor rotation about the center, same dimensions, white fill. */
export function rotateImageData(src: ImageData, radians: number): ImageData {
  if (radians === 0) return src;
  const { width: w, height: h, data } = src;
  const out = blank(w, h);
  const cos = Math.cos(-radians);
  const sin = Math.sin(-radians);
  const cx = w / 2;
  const cy = h / 2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const sxF = cx + dx * cos - dy * sin;
      const syF = cy + dx * sin + dy * cos;
      const sx = Math.round(sxF);
      const sy = Math.round(syF);
      if (sx < 0 || sx >= w || sy < 0 || sy >= h) continue;
      const from = (sy * w + sx) * 4;
      const to = (y * w + x) * 4;
      out.data[to] = data[from];
      out.data[to + 1] = data[from + 1];
      out.data[to + 2] = data[from + 2];
    }
  }
  return out;
}

/**
 * Tilt of 1D bars from vertical, in radians, via the structure tensor:
 * the dominant gradient direction of a bar pattern is perpendicular to the
 * bars, so its angle from horizontal equals the bars' tilt from vertical.
 */
export function estimateBarAngle(img: ImageData): number {
  const { width: w, height: h, data } = img;
  let gxx = 0;
  let gyy = 0;
  let gxy = 0;
  const stepY = Math.max(1, Math.floor(h / 64));
  const stepX = Math.max(1, Math.floor(w / 128));
  for (let y = 1; y < h - 1; y += stepY) {
    for (let x = 1; x < w - 1; x += stepX) {
      const i = (y * w + x) * 4 + 1; // green ≈ luma
      const gx = data[i + 4] - data[i - 4];
      const gy = data[i + w * 4] - data[i - w * 4];
      gxx += gx * gx;
      gyy += gy * gy;
      gxy += gx * gy;
    }
  }
  return 0.5 * Math.atan2(2 * gxy, gxx - gyy);
}
